import type { ReactNode } from 'react';
import { AlertTriangle, LoaderCircle } from 'lucide-react';
import { cn } from './cn.js';
import { Button } from './button.js';

export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-[var(--nw-quiet)]', className)}>
      <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
      <span className="text-sm">{label}</span>
    </span>
  );
}

/** Explains what failed and offers the specific way forward. Never vague, never apologetic. */
export function ErrorNotice({ title, detail, onRetry, retryLabel = 'Try again', className }: { title: string; detail?: ReactNode; onRetry?: () => void; retryLabel?: string; className?: string }) {
  return (
    <div role="alert" className={cn('flex items-start gap-3 rounded-[var(--nw-radius-panel)] border border-[var(--nw-danger)]/40 bg-[var(--nw-danger-soft)] p-4 text-[var(--nw-ink)]', className)}>
      <AlertTriangle className="mt-0.5 size-5 shrink-0 text-[var(--nw-danger)]" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        {detail ? <div className="mt-1 text-sm text-[var(--nw-quiet)]">{detail}</div> : null}
      </div>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function Chip({ selected, children, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        'inline-flex h-9 min-w-11 items-center justify-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors duration-[var(--nw-dur-feedback)]',
        selected
          ? 'border-[var(--nw-ink)] bg-[var(--nw-ink)] text-[var(--nw-on-ink)]'
          : 'border-[var(--nw-line-strong)] text-[var(--nw-quiet)] hover:border-[var(--nw-ink)] hover:text-[var(--nw-ink)]',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/** Visible marker for fixture/demo content so it is never mistaken for real data. */
export function DemoBadge({ children = 'Demo', className }: { children?: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border border-dashed border-current px-2 py-0.5 text-xs font-semibold', className)}>{children}</span>
  );
}
